import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { fetchDataCenterList } from "./requests/request";
import {
  datacenterColumns as baseColumns,
  DataCenterConnection,
  shapeDatacenterData,
} from "./DatacenterData";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import { makeDatacenterFields } from "./datacenterFieldSpec";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

interface MainDataCenter {
  text: string;
}

const MainDataCenter: React.FC = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [datacenterList, setDatacenterList] = useState<DataCenterConnection[]>(
    []
  );
  const overlaySpinner = useProcessingOverlay(isFetching);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);


  const dcFields = useMemo(
    () => makeDatacenterFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const normalized = useMemo(
    () => normalizeFilters<DataCenterConnection>(activeFilters, dcFields, {
      defaultStringToContains: true,
      quotedStringsExact: true,
    }),
    [activeFilters, dcFields]
  );

  const predicate = useMemo(
    () => buildPredicate<DataCenterConnection>(normalized, dcFields),
    [normalized, dcFields]
  );

  const filteredData = useMemo(
    () => datacenterList.filter(predicate),
    [datacenterList, predicate]
  );

  const suggestedKeys = useMemo(
    () => dcFields.map(f => f.label),
    [dcFields]
  );
  const columns = useMemo<GenericTableColumn<DataCenterConnection>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: DataCenterConnection) => row.customer?.name ?? "",
        renderCell: (row: DataCenterConnection) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    return cols.map((col) => {
      // 1. Override the “Service ID” column to navigate on click
      if (col.header === "DCI ID") {
        return {
          ...col,
          renderCell: (row: DataCenterConnection) => (
            <button
              onClick={() => {
              // CASE A: Partner view (showing ALL customers)
							//   → pick row.customerId (e.g. 11) instead of tenantId
							// CASE B: Customer view (only seeing your own)
							//   → use the Redux‐provided tenantId
              const idFromRow = Number(row.customer?.id ?? NaN);
              const nextTenantId =
                isViewingAllCustomers && Number.isFinite(idFromRow)
                  ? idFromRow
                  : tenantId;
                  navigate(
                    `/kontrol/datacenter-details?datacenterId=${row.id}`,
                    {
                      state: {
                        datacenterDetail: row,
                        tenantOverride: nextTenantId,
                      },
                    }
                  );
              }}
              className="
            hover:text-semantic-text-brand 
            hover:underline
            focus:underline 
            active:underline
            font-medium
            underline
            text-semantic-text-brand
            "
            >
              {row.subscriptionId}
            </button>
          ),
        };
      }
      // 2. Override the “Parent connection” column to navigate on click
      if (col.header === "Parent Connection") {
        return {
          ...col,
          renderCell: (row: DataCenterConnection) => {
            const parent = row.parentConnection;

            if (!parent) return "—";
            return (
              <div className="flex flex-wrap gap-x-2">
                {parent.labelA && parent.idA && (
                  <button
                    onClick={() =>
                      navigate(`/kontrol/port-details?portId=${parent.idA}&subid=${encodeURIComponent(parent.labelA ?? "")}`)
                    }
                    className="hover:text-semantic-text-brand hover:underline font-medium text-semantic-text-brand underline"
                  >
                    {parent.labelA} ,
                  </button>
                )}
                {parent.labelB &&
                  (() => {
                    // Route the B endpoint by kind: SD-WAN hub -> hub details,
                    // port (or legacy) -> port details, FWAAS -> no per-id
                    // detail route (plain text).
                    let route: string | null = null;
                    if (parent.kindB === "sdwan_hub" && parent.idB) {
                      route = `/kontrol/sdwan-hub-details?sdwanId=${parent.idB}`;
                    } else if (
                      (!parent.kindB || parent.kindB === "port") &&
                      parent.idB
                    ) {
                      route = `/kontrol/port-details?portId=${parent.idB}&subid=${encodeURIComponent(parent.labelB ?? "")}`;
                    }
                    return route ? (
                      <button
                        onClick={() => navigate(route as string)}
                        className="hover:text-semantic-text-brand hover:underline font-medium text-semantic-text-brand underline"
                      >
                        {parent.labelB}
                      </button>
                    ) : (
                      <span className="font-medium text-semantic-text-primary">
                        {parent.labelB}
                      </span>
                    );
                  })()}
              </div>
            );
          },
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers]);

  const isDemo = isDemoClient();
  const dciColumnVisibility = useColumnVisibility(
    "kontrol:dci:columns",
    columns,
    ["DCI ID"],
  );
  const tableColumns = isDemo ? dciColumnVisibility.visibleColumns : columns;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetching(true);
        setFetchError(null);
        const response = await fetchDataCenterList(api, tenantId);
        const { status, data } = response;
        if (status >= 500 || data.success === false) {
          setFetchError(data.message || "Unknown server error");
          setDatacenterList([]);
        }
        //Valid array payload
        else if (Array.isArray(data.data)) {
          setDatacenterList(shapeDatacenterData(data.data));
        }
        //Some other unexpected shape
        else {
          setDatacenterList([]);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.error("Error fetching table ports", err);
        setFetchError(err.message || "Network error");
        setDatacenterList([]);
      } finally {
        setIsFetching(false);
        setHasFetched(true);
      }
    };
    fetchData();
  }, [getAccessTokenSilently, tenantId]);

  //Cases to handle the fetching and the display of empty items
  // If data is being fetched, show only the overlay spinner.
  if (!hasFetched || isFetching) {
    return overlaySpinner;
  }
  //If there's an error in the request
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  //If no ports are returned, display the empty state.
  if (datacenterList.length === 0) {
    return <EmptyItems />;
  }
  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">

      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">

              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-3 my-5">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder='Find by attribute or value'
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                      liveFiltering
                      debounceMs={200}
                    />
                  </div>
                  {isDemo && (
                    <ColumnVisibilityControl
                      columns={columns}
                      isVisible={dciColumnVisibility.isVisible}
                      toggle={dciColumnVisibility.toggle}
                      requiredIds={["DCI ID"]}
                    />
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={tableColumns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainDataCenter;
